import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SkillsViewMode } from '../../types';
import type { SkillsPanelKind, SkillsSkeletonKind } from './skillsSkeletonKind';
import { NS } from './i18n';
import './SkillsSkeleton.css';

/*
 * Local counts, not the module's constants: `constants.ts` imports the instructions
 * model, and this file is in the initial bundle.
 */
const LIST_ROWS = [0, 1, 2, 3];
const LIST_TABS = [0, 1, 2];
const STATUS_PILLS = [0, 1, 2];
const VIEW_PILLS = [0, 1];
const VAULT_PILLS = [0, 1];
const VAULT_ROWS = [0, 1, 2, 3];
const VAULT_CELLS = [0, 1, 2, 3, 4];
const LOG_FILTERS = [0, 1, 2, 3, 4, 5];
const LOG_ROWS = [0, 1, 2, 3, 4];
const LOG_CELLS = [0, 1, 2, 3, 4];
const RUN_STEPS = [0, 1, 2];
const EDITOR_ACTIONS = [0, 1];
const EDITOR_TABS = [0, 1];
const META_FIELDS = [0, 1];
const INSTRUCTION_LINES = ['long', 'medium', 'long', 'short', 'long', 'medium'] as const;
const TOOL_TABS = [0, 1, 2];
const TOOL_ROWS = [0, 1, 2, 3];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`skills-skeleton__bone ${className}`} />
);

/** A Segmented bar: a bordered pill holding `count` options. */
const Segmented: React.FC<{ items: readonly number[] }> = ({ items }) => (
  <div className="skills-skeleton__segmented">
    {items.map((item) => (
      <Bone key={item} className="skills-skeleton__segment" />
    ))}
  </div>
);

/** A bordered table: one header row, then `rows`, each with `cells`. */
const TableBones: React.FC<{
  variant: 'vault' | 'logs';
  rows: readonly number[];
  cells: readonly number[];
}> = ({ variant, rows, cells }) => (
  <div className={`skills-skeleton__table skills-skeleton__table--${variant}`}>
    {[-1, ...rows].map((row) => (
      <div
        key={row}
        className={`skills-skeleton__row${row < 0 ? ' skills-skeleton__row--head' : ''}`}
      >
        {cells.map((cell) => (
          <Bone key={cell} className="skills-skeleton__cell" />
        ))}
      </div>
    ))}
  </div>
);

/** The breadcrumb, then the page card the content sits in. */
const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <div className="skills-skeleton__header" aria-hidden="true">
      <Bone className="skills-skeleton__crumb" />
    </div>
    <div className="skills-skeleton__card" aria-hidden="true">
      {children}
    </div>
  </>
);

/** The page heading: title and subtitle, and the primary action on the right when there is one. */
const Heading: React.FC<{ action?: boolean; lines?: number }> = ({ action = false, lines = 1 }) => (
  <div className="skills-skeleton__head">
    <div className="skills-skeleton__heading">
      <Bone className="skills-skeleton__title" />
      {Array.from({ length: lines }, (_, line) => (
        <Bone key={line} className="skills-skeleton__line skills-skeleton__line--subtitle" />
      ))}
    </div>
    {action && <Bone className="skills-skeleton__button" />}
  </div>
);

// ── Panels: what each screen draws under its heading ─────────────────────

/** The skills themselves, as dense rows (SkillRow) or a grid of cards (SkillCard). */
function ListPanel({ view }: { view: SkillsViewMode }): React.ReactElement {
  if (view === 'grid') {
    return (
      <div className="skills-skeleton__panel skills-skeleton__grid">
        {LIST_ROWS.map((card) => (
          <div key={card} className="skills-skeleton__tile">
            <div className="skills-skeleton__tile-head">
              <Bone className="skills-skeleton__icon" />
              <Bone className="skills-skeleton__pill" />
            </div>
            <Bone className="skills-skeleton__line skills-skeleton__line--medium" />
            <Bone className="skills-skeleton__line skills-skeleton__line--long" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="skills-skeleton__panel skills-skeleton__list">
      {LIST_ROWS.map((row) => (
        <div key={row} className="skills-skeleton__item">
          <Bone className="skills-skeleton__icon" />
          <div className="skills-skeleton__item-text">
            <Bone className="skills-skeleton__line skills-skeleton__line--medium" />
            <Bone className="skills-skeleton__line skills-skeleton__line--long" />
          </div>
          <Bone className="skills-skeleton__pill" />
          <Bone className="skills-skeleton__meta" />
          <Bone className="skills-skeleton__menu" />
        </div>
      ))}
    </div>
  );
}

/** Secrets & variables: sub-tabs, search and New, the banner line, then the entries. */
function VaultPanel(): React.ReactElement {
  return (
    <div className="skills-skeleton__panel">
      <div className="skills-skeleton__toolbar">
        <Segmented items={VAULT_PILLS} />
        <Bone className="skills-skeleton__search" />
        <Bone className="skills-skeleton__button skills-skeleton__button--end" />
      </div>
      <Bone className="skills-skeleton__line skills-skeleton__line--medium" />
      <TableBones variant="vault" rows={VAULT_ROWS} cells={VAULT_CELLS} />
    </div>
  );
}

/** A skill's log: the runs table with its footer, then the selected run's steps. */
function LogsPanel(): React.ReactElement {
  return (
    <div className="skills-skeleton__panel">
      <div className="skills-skeleton__table-box">
        <TableBones variant="logs" rows={LOG_ROWS} cells={LOG_CELLS} />
        <div className="skills-skeleton__table-footer">
          <Bone className="skills-skeleton__line skills-skeleton__line--short" />
          <Bone className="skills-skeleton__button" />
        </div>
      </div>
      <div className="skills-skeleton__detail">
        <div className="skills-skeleton__detail-head">
          <div className="skills-skeleton__heading">
            <Bone className="skills-skeleton__box-title" />
            <Bone className="skills-skeleton__line skills-skeleton__line--medium" />
          </div>
          <Bone className="skills-skeleton__pill" />
        </div>
        {RUN_STEPS.map((step) => (
          <div key={step} className="skills-skeleton__step">
            <Bone className="skills-skeleton__dot" />
            <Bone className="skills-skeleton__line skills-skeleton__line--medium" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The editor's card body: name and actions, Description and Goal, the
 * Instructions / Business rules switch, then the editor beside the tools panel.
 */
function EditorPanel(): React.ReactElement {
  return (
    <div className="skills-skeleton__panel skills-skeleton__panel--editor">
      <div className="skills-skeleton__editor-head">
        <Bone className="skills-skeleton__name" />
        <div className="skills-skeleton__actions">
          {EDITOR_ACTIONS.map((action) => (
            <Bone key={action} className="skills-skeleton__button" />
          ))}
          <Segmented items={EDITOR_TABS} />
          <Bone className="skills-skeleton__button" />
        </div>
      </div>
      <div className="skills-skeleton__fields">
        {META_FIELDS.map((field) => (
          <div key={field} className="skills-skeleton__field">
            <Bone className="skills-skeleton__label" />
            <Bone className="skills-skeleton__control" />
          </div>
        ))}
      </div>
      <Segmented items={EDITOR_TABS} />
      <div className="skills-skeleton__workspace">
        <div className="skills-skeleton__instructions">
          <Bone className="skills-skeleton__control" />
          {INSTRUCTION_LINES.map((width, line) => (
            <Bone key={line} className={`skills-skeleton__line skills-skeleton__line--${width}`} />
          ))}
        </div>
        <div className="skills-skeleton__tools">
          <Segmented items={TOOL_TABS} />
          {TOOL_ROWS.map((row) => (
            <div key={row} className="skills-skeleton__step">
              <Bone className="skills-skeleton__icon" />
              <Bone className="skills-skeleton__line skills-skeleton__line--long" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Exhaustive: a new kind fails to type-check until it has a panel. */
const PANELS: Record<SkillsPanelKind, (view: SkillsViewMode) => React.ReactElement> = {
  list: (view) => <ListPanel view={view} />,
  editor: () => <EditorPanel />,
  logs: () => <LogsPanel />,
  vault: () => <VaultPanel />,
};

const PanelBones: React.FC<{ kind: SkillsPanelKind; view: SkillsViewMode }> = ({ kind, view }) =>
  PANELS[kind](view);

/** The whole screen: what sits above each panel on the real page. */
const ScreenBones: React.FC<{ kind: SkillsSkeletonKind }> = ({ kind }) => {
  if (kind === 'editor') return <EditorPanel />;
  if (kind === 'logs') {
    return (
      <>
        <Heading lines={2} />
        <Segmented items={LOG_FILTERS} />
        <LogsPanel />
      </>
    );
  }
  return (
    <>
      <Heading action />
      <div className="skills-skeleton__tabs">
        {LIST_TABS.map((tab) => (
          <Bone key={tab} className="skills-skeleton__tab" />
        ))}
      </div>
      <div className="skills-skeleton__toolbar">
        <Bone className="skills-skeleton__search" />
        <Segmented items={STATUS_PILLS} />
        <Bone className="skills-skeleton__select" />
        <Segmented items={VIEW_PILLS} />
      </div>
      <ListPanel view="table" />
    </>
  );
};

export interface SkillsPanelSkeletonProps {
  kind: SkillsPanelKind;
  /** The list's view mode, so its loading rows match what will replace them. */
  view?: SkillsViewMode;
}

/**
 * One panel's loading shape, for content that loads inside a drawn page: the list's
 * rows, the vault tab, a skill's log, or the editor's card body. It is the only
 * live region there, so its parent must not carry `aria-busy`.
 */
export const SkillsPanelSkeleton: React.FC<SkillsPanelSkeletonProps> = ({
  kind,
  view = 'table',
}) => {
  const { t } = useTranslation(NS);
  const text = t(kind);

  return (
    <div
      className="skills-skeleton"
      role="status"
      data-testid="skills-panel-skeleton"
      data-kind={kind}
    >
      <span className="skills-skeleton__sr-only">{text}</span>
      <div className="skills-skeleton__body" aria-hidden="true">
        <PanelBones kind={kind} view={view} />
      </div>
    </div>
  );
};

export interface SkillsSkeletonProps {
  kind: SkillsSkeletonKind;
  /** Adds the agent screens' gutter, for RouteSkeleton, which draws it straight in the app chrome. */
  standalone?: boolean;
}

/**
 * Loading shape of a Skills screen: breadcrumb, then the card with that screen's
 * heading and panel. The route fallback, RouteSkeleton and the agent layout all
 * draw this one, so a cold load keeps one shape. No `aria-busy`: on a live region
 * it holds the announcement back.
 */
export const SkillsSkeleton: React.FC<SkillsSkeletonProps> = ({ kind, standalone = false }) => {
  const { t } = useTranslation(NS);
  const text = t(kind);

  return (
    <div
      className={`skills-skeleton${standalone ? ' skills-skeleton--standalone' : ''}`}
      role="status"
      data-testid="skills-skeleton"
      data-kind={kind}
    >
      <span className="skills-skeleton__sr-only">{text}</span>
      <Frame>
        <ScreenBones kind={kind} />
      </Frame>
    </div>
  );
};
